import { OTHER_MEETINGS_BUCKET_ID, type TCreditTransactionKind } from "@lexicanon/shared"
import { msg } from "@lingui/core/macro"
import { useLingui } from "@lingui/react"
import { Plural, Trans } from "@lingui/react/macro"
import { Badge, SimpleGrid, Stack, Text } from "@mantine/core"
import { StatTile } from "../../../../components/StatTile"
import { formatDate, formatInteger } from "../../../../lib/format-date"
import { deriveBalanceTone, deriveExpiringSoon, deriveSpendPace } from "../credits-helpers"
import type { IProps } from "./interfaces"

/** The health chip beside the balance — colour + label from the wallet tone. */
function healthChip(tone: ReturnType<typeof deriveBalanceTone>): React.JSX.Element {
  switch (tone) {
    case "healthy":
      return (
        <Badge variant="light" color="success" size="sm">
          <Trans>Healthy</Trans>
        </Badge>
      )
    case "low":
      return (
        <Badge variant="light" color="warning" size="sm">
          <Trans>Getting low</Trans>
        </Badge>
      )
    case "empty":
      return (
        <Badge variant="light" color="danger" size="sm">
          <Trans>Empty</Trans>
        </Badge>
      )
    case "pilot":
      return (
        <Badge variant="light" color="info" size="sm">
          <Trans>Free during the pilot</Trans>
        </Badge>
      )
  }
}

/** Friendly source label for an expiring lot — grants renew monthly, packs are prepaid. */
function lotSourceLabel(i18n: ReturnType<typeof useLingui>["i18n"], kind: TCreditTransactionKind): string {
  return kind === "purchase" ? i18n._(msg`credit pack`) : i18n._(msg`monthly grant`)
}

/**
 * The four-tile health band at the top of the credits page — balance, spend this
 * period, projected runway, and the nearest upcoming expiry. Pure presentation:
 * it derives everything from the wallet + statement via the tested helpers, so a
 * given pair of props always renders the same band. CREDITS ONLY.
 */
export const CreditKpiBand = ({
  wallet,
  enforcementEnabled,
  statement,
  periodFrom,
  periodTo,
  now,
}: IProps): React.JSX.Element => {
  const { i18n } = useLingui()
  const clock = now ?? new Date()

  const tone = wallet
    ? deriveBalanceTone(wallet.balanceCredits, wallet.projectedZeroAt, clock, enforcementEnabled)
    : null
  const pace =
    statement !== null
      ? deriveSpendPace(
          statement.totalCredits,
          periodFrom,
          periodTo,
          clock,
          enforcementEnabled,
          wallet?.balanceCredits ?? 0,
          wallet?.projectedZeroAt ?? null,
        )
      : null
  const expiring = wallet ? deriveExpiringSoon(wallet.liveLots, clock) : null
  // A restricted member's statement folds the meetings they can't see into one
  // aggregate row, so the true meeting count is unknowable here — drop the
  // "across N meetings" line entirely rather than quietly undercounting.
  const hasHiddenMeetings = statement?.byMeeting.some((row) => row.meetingId === OTHER_MEETINGS_BUCKET_ID) ?? false
  const meetingCount = hasHiddenMeetings ? 0 : (statement?.byMeeting.length ?? 0)

  return (
    <SimpleGrid cols={{ base: 2, md: 4 }} spacing="md">
      <StatTile
        label={<Trans>Balance</Trans>}
        value={wallet ? formatInteger(i18n, wallet.balanceCredits) : "—"}
        valueSuffix={wallet ? <Trans>credits</Trans> : undefined}
        meta={
          tone === "pilot" ? (
            <Stack gap={4}>
              {healthChip(tone)}
              <Text size="xs" c="dimmed">
                <Trans>Nothing is charged yet</Trans>
              </Text>
            </Stack>
          ) : tone ? (
            healthChip(tone)
          ) : undefined
        }
      />

      <StatTile
        label={<Trans>Spent this period</Trans>}
        value={statement ? formatInteger(i18n, statement.totalCredits) : "—"}
        meta={
          statement && meetingCount > 0 ? (
            <Plural value={meetingCount} one="across # meeting" other="across # meetings" />
          ) : undefined
        }
      />

      <StatTile
        label={<Trans>At this pace</Trans>}
        value={
          statement === null ? (
            "—"
          ) : pace ? (
            <Trans>≈ {formatInteger(i18n, pace.projectedCredits)}</Trans>
          ) : (
            <Trans>Too early to tell</Trans>
          )
        }
        meta={
          pace ? (
            <Stack gap={2}>
              <Text size="xs" c="dimmed">
                <Trans>by {formatDate(i18n, pace.monthEnd, { year: false })}</Trans>
              </Text>
              {pace.runOutAt ? (
                <Text size="xs" c="dimmed">
                  <Trans>runs out around {formatDate(i18n, pace.runOutAt, { year: false })}</Trans>
                </Text>
              ) : null}
            </Stack>
          ) : undefined
        }
      />

      <StatTile
        label={<Trans>Expiring soon</Trans>}
        value={expiring ? formatInteger(i18n, expiring.credits) : wallet ? formatInteger(i18n, 0) : "—"}
        meta={
          expiring ? (
            <Badge variant="light" color={expiring.soon ? "warning" : "info"} size="sm">
              {formatDate(i18n, expiring.expiresAt, { year: false })} — {lotSourceLabel(i18n, expiring.kind)}
            </Badge>
          ) : (
            <Trans>Nothing expiring</Trans>
          )
        }
      />
    </SimpleGrid>
  )
}
